import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import AlertModal from "../components/modals/AlertModal";
import ConfirmModal from "../components/modals/ConfirmModal";
import SelectModal from "../components/modals/SelectModal";
import PromptModal from "../components/modals/PromptModal";
import Toast, { ToastType } from "../components/ui/Toast";

interface AlertOptions {
  title: string;
  message: string;
  type?: "success" | "error" | "warning" | "info";
  buttonText?: string;
}

interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonVariant?: "danger" | "primary" | "warning";
}

interface SelectOptions {
  title: string;
  message: string;
  options: string[];
}

interface PromptOptions {
  title: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  multiline?: boolean;
}

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
}

interface ModalContextType {
  showAlert: (options: AlertOptions) => Promise<void>;
  showConfirm: (options: ConfirmOptions) => Promise<boolean>;
  showSelect: (options: SelectOptions) => Promise<string>;
  showPrompt: (options: PromptOptions) => Promise<string>;
  showToast: (message: string, type?: ToastType, durationMs?: number) => void;
}

const ModalContext = createContext<ModalContextType | null>(null);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    options: AlertOptions | null;
    resolve: (() => void) | null;
  }>({
    isOpen: false,
    options: null,
    resolve: null,
  });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    options: ConfirmOptions | null;
    resolve: ((value: boolean) => void) | null;
  }>({
    isOpen: false,
    options: null,
    resolve: null,
  });

  const [selectModal, setSelectModal] = useState<{
    isOpen: boolean;
    options: SelectOptions | null;
    resolve: ((value: string) => void) | null;
  }>({
    isOpen: false,
    options: null,
    resolve: null,
  });

  const [promptModal, setPromptModal] = useState<{
    isOpen: boolean;
    options: PromptOptions | null;
    resolve: ((value: string) => void) | null;
  }>({
    isOpen: false,
    options: null,
    resolve: null,
  });

  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showAlert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      setAlertModal({
        isOpen: true,
        options,
        resolve,
      });
    });
  }, []);

  const showConfirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmModal({
        isOpen: true,
        options,
        resolve,
      });
    });
  }, []);

  const showSelect = useCallback((options: SelectOptions): Promise<string> => {
    return new Promise((resolve) => {
      setSelectModal({ isOpen: true, options, resolve });
    });
  }, []);

  const showPrompt = useCallback((options: PromptOptions): Promise<string> => {
    return new Promise((resolve) => {
      setPromptModal({ isOpen: true, options, resolve });
    });
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info', durationMs = 4000) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, type }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, durationMs);
  }, []);

  const handleToastClose = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleAlertClose = () => {
    setAlertModal((prev) => {
      if (prev.resolve) prev.resolve();
      return { isOpen: false, options: null, resolve: null };
    });
  };

  const handleConfirmClose = () => {
    setConfirmModal((prev) => {
      if (prev.resolve) prev.resolve(false);
      return { isOpen: false, options: null, resolve: null };
    });
  };

  const handleSelectClose = () => {
    setSelectModal((prev) => {
      if (prev.resolve) prev.resolve("");
      return { isOpen: false, options: null, resolve: null };
    });
  };

  const handlePromptClose = () => {
    setPromptModal((prev) => {
      if (prev.resolve) prev.resolve("");
      return { isOpen: false, options: null, resolve: null };
    });
  };

  const handleSelectConfirm = (option: string) => {
    setSelectModal((prev) => {
      if (prev.resolve) prev.resolve(option);
      return { isOpen: false, options: null, resolve: null };
    });
  };

  const handlePromptConfirm = (value: string) => {
    setPromptModal((prev) => {
      if (prev.resolve) prev.resolve(value);
      return { isOpen: false, options: null, resolve: null };
    });
  };

  const handleConfirmConfirm = () => {
    setConfirmModal((prev) => {
      if (prev.resolve) prev.resolve(true);
      return { isOpen: false, options: null, resolve: null };
    });
  };

  const modalContextValue = useMemo(
    () => ({ showAlert, showConfirm, showSelect, showPrompt, showToast }),
    [showAlert, showConfirm, showSelect, showPrompt, showToast]
  );

  return (
    <ModalContext.Provider value={modalContextValue}>
      {children}

      {alertModal.options && (
        <AlertModal
          isOpen={alertModal.isOpen}
          onClose={handleAlertClose}
          title={alertModal.options.title}
          message={alertModal.options.message}
          type={alertModal.options.type}
          buttonText={alertModal.options.buttonText}
        />
      )}

      {confirmModal.options && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={handleConfirmClose}
          onConfirm={handleConfirmConfirm}
          title={confirmModal.options.title}
          message={confirmModal.options.message}
          confirmText={confirmModal.options.confirmText}
          cancelText={confirmModal.options.cancelText}
          confirmButtonVariant={confirmModal.options.confirmButtonVariant}
        />
      )}

      {selectModal.options && (
        <SelectModal
          title={selectModal.options.title}
          message={selectModal.options.message}
          isOpen={selectModal.isOpen}
          onClose={handleSelectClose}
          onConfirm={handleSelectConfirm}
          options={selectModal.options.options}
        />
      )}

      {promptModal.options && (
        <PromptModal
          isOpen={promptModal.isOpen}
          onClose={handlePromptClose}
          onConfirm={handlePromptConfirm}
          title={promptModal.options.title}
          message={promptModal.options.message}
          placeholder={promptModal.options.placeholder}
          defaultValue={promptModal.options.defaultValue}
          confirmText={promptModal.options.confirmText}
          cancelText={promptModal.options.cancelText}
          multiline={promptModal.options.multiline}
        />
      )}

      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-3">
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              id={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={handleToastClose}
            />
          ))}
        </div>
      )}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
}
